import { ChevronDown, FileText, Menu, User, LogOut } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { getRouteByPath } from "../../config/routes";
import { useAuth } from "../../contexts/AuthContext";
import { useState, useRef, useEffect } from "react";
import logo from "../../assets/logo.png";

interface HeaderProps {
  onMenuClick: () => void;
}

export function Header({ onMenuClick }: HeaderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const currentRoute = getRouteByPath(location.pathname);
  const { logout } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleLogout = () => {
    logout();
    navigate("/login");
    setIsDropdownOpen(false);
  };

  return (
    <header className="h-16 lg:h-20 bg-white flex items-center justify-between px-4 lg:px-6 border-b border-gray-100 lg:border-none">
      <div className="flex items-center gap-4">
        {/* Hamburger Menu - Mobile Only */}
        <button
          onClick={onMenuClick}
          className="lg:hidden p-2 -ml-2 text-gray-600 hover:bg-gray-100 rounded-lg"
        >
          <Menu size={24} />
        </button>

        {/* Logo - Mobile */}
        <div className="lg:hidden">
          <img src={logo} alt="AH Planning" className="h-8" />
        </div>

        {/* Breadcrumbs - Desktop */}
        <div className="hidden lg:flex items-center gap-2 text-sm">
          <FileText size={16} className="text-[#989797]" />
          <span className="text-[#989797] font-normal">pages</span>
          <span className="text-[#989797] font-normal">/</span>
          <span className="text-[#f09040] font-medium">
            {currentRoute?.breadcrumb || "Dashboard"}
          </span>
        </div>
      </div>

      {/* Right Section: Profile Dropdown */}
      <div className="flex items-center gap-4 lg:gap-8">
        {/* User Profile with Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex items-center gap-3 focus:outline-none hover:bg-gray-50 rounded-lg p-2 transition-colors"
          >
            <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-full bg-indigo-100 flex items-center justify-center">
              <User size={20} className="text-indigo-600" />
            </div>
            <div className="text-left hidden md:block">
              <p className="text-sm font-medium text-[#102040] leading-none">
                Admin
              </p>
            </div>
            <ChevronDown
              size={16}
              className={`text-[#989797] transition-transform ${
                isDropdownOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setIsDropdownOpen(false)}
              />
              <div className="absolute right-0 top-full mt-2 z-20 bg-white border border-gray-100 rounded-lg shadow-xl min-w-[180px] animate-in fade-in zoom-in duration-150">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <LogOut size={16} />
                  Logout
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
